import React, { useState, useMemo, useCallback } from 'react'
import { Row, Col, Card, Dropdown } from 'react-bootstrap'
import uniq from 'lodash/uniq'
import { useTranslation } from 'react-i18next'
import { CHART_CATEGORY_KEYS } from '../../constants'
import styles from './ChartSelector.module.scss'

const ALL_CHARTS_FILTER = '__all__'

function filterCharts(charts, filter) {
  return filter === ALL_CHARTS_FILTER
    ? charts
    : charts.filter((d) => d.metadata.categories.indexOf(filter) !== -1)
}

function ChartSelector({ availableCharts, currentChart, setCurrentChart }) {
  const { t } = useTranslation()
  const [filter, setFilter] = useState(ALL_CHARTS_FILTER)

  const charts = useMemo(() => {
    return filterCharts(availableCharts, filter)
  }, [availableCharts, filter])

  const handleFilterChange = useCallback(
    (nextFilter) => {
      setFilter(nextFilter)
      const nextCharts = filterCharts(availableCharts, nextFilter)
      if (nextCharts.indexOf(currentChart) === -1) {
        setCurrentChart(nextCharts[0])
      }
    },
    [availableCharts, currentChart, setCurrentChart]
  )

  const currentChartName =
    currentChart?.metadata.displayName || currentChart?.metadata.name

  return (
    <>
      <Row>
        <Col className="text-right">
          {t('chartSelector.filter')}
          <Dropdown className="d-inline-block ml-2 raw-dropdown">
            <Dropdown.Toggle variant="white" className="pr-5">
              {filter === ALL_CHARTS_FILTER ? t('chartSelector.allCharts') : (CHART_CATEGORY_KEYS[filter] ? t(CHART_CATEGORY_KEYS[filter]) : filter)}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                key={ALL_CHARTS_FILTER}
                onClick={() => handleFilterChange(ALL_CHARTS_FILTER)}
              >
                {t('chartSelector.allCharts')}
              </Dropdown.Item>
              {uniq(
                availableCharts.map((d) => d.metadata.categories).flat()
              ).map((d) => {
                return (
                  <Dropdown.Item key={d} onClick={() => handleFilterChange(d)}>
                    {CHART_CATEGORY_KEYS[d] ? t(CHART_CATEGORY_KEYS[d]) : d.charAt(0).toUpperCase() + d.slice(1)}
                  </Dropdown.Item>
                )
              })}
            </Dropdown.Menu>
          </Dropdown>
        </Col>
      </Row>
      <Row>
        <Col xs={3} className="pt-3">
          {currentChart && (
            <Card className={styles.currentChart}>
              <Card.Img variant="top" src={currentChart.metadata.thumbnail} />
              <Card.Body>
                <Card.Title className="m-0">
                  <h2 className="m-0">{currentChartName}</h2>
                </Card.Title>
                <Card.Subtitle className="m-0">
                  <h4 className="mb-2">{currentChart.metadata.category}</h4>
                </Card.Subtitle>
                <Card.Text>{currentChart.metadata.description}</Card.Text>
              </Card.Body>
            </Card>
          )}
        </Col>
        <Col>
          <Row>
            {charts.map((d, i) => {
              return (
                <Col xs={4} key={'chart-' + i} className={`p-3`}>
                  <Card
                    onClick={() => {
                      setCurrentChart(d)
                    }}
                    className={`flex-row h-100 cursor-pointer ${d === currentChart ? 'active' : ''
                      }`}
                  >
                    <div
                      className={`h-100 w-25 ${styles.thumbnail}`}
                      style={{ backgroundImage: `url("${d.metadata.icon}")` }}
                    ></div>
                    <Card.Body className="w-75 px-2 py-3">
                      <Card.Title className="m-0">
                        <h2 className="m-0" style={{ whiteSpace: 'nowrap' }}>
                          {d.metadata.displayName || d.metadata.name}
                        </h2>
                      </Card.Title>
                      <Card.Subtitle className="m-0">
                        <h4 className="m-0">
                          {d.metadata.categories
                            .map((cat) => CHART_CATEGORY_KEYS[cat] ? t(CHART_CATEGORY_KEYS[cat]) : cat.charAt(0).toUpperCase() + cat.slice(1))
                            .join(', ')}
                        </h4>
                      </Card.Subtitle>
                    </Card.Body>
                  </Card>
                </Col>
              )
            })}
          </Row>
        </Col>
      </Row>
    </>
  )
}

export default React.memo(ChartSelector)
